import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useExportPensionAssessed } from '@nxsys/hooks/api/report';
import useExportAllSocket from '@nxsys/hooks/useExportAllSocket';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetAutoEnrollmentStatusListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { formatFullName, showCurrencyByCountry } from '@nxsys/utils';
import { pensionProviderIdOptions } from '@nxsys/constants/AppConstants';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExportOrNull } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce, isNumber } from 'lodash';
import AutoEnrollmentFilterModal, {
  AutoEnrollmentFilterValues,
} from '../../payroll/components/AutoEnrollmentFilterModal';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAutoEnrollmentStatusColumns } from './columns';

const AutoEnrollmentStatusReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { payrollCountry, user, isBureauEmployer, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const dispatch = useDispatch();
  const { autoEnrollmentStatusList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = autoEnrollmentStatusList;

  const [selectedRows, setSelectedRows] = useState<IPensionAssessed[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingExport, run: exportPensionAssessed } =
    useExportPensionAssessed();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.autoEnrolmentStatusReport']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetAutoEnrollmentStatusListParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
      };

      dispatch(reportActions.getAutoEnrollmentStatusList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: AutoEnrollmentFilterValues) => {
    const params: GetAutoEnrollmentStatusListParams = {
      ...queryParams,
      companyIds: values.companies?.map((c) => c.value),
      pensionProviderId: values.pensionProvider?.value,
    };
    handleFetchData(params);
  };

  const handleExport = (data: IPensionAssessed[]) => {
    const dataExport = data.map((item) => {
      const {
        subcontractor,
        companyPayment,
        otpOutDate,
        otpInDate,
        memberId,
        membershipStartDate,
        pensionProviderId,
        pensionSchemeId,
        pensionSchemeName,
        postponementEndDate,
        assessStatus,
        assessedAt,
        category,
        categoryChangedAt,
        ejhDate,
        eePercent,
        erPercent,
        refundDue,
        refundAmount,
        isRefundPaid,
        refundPaidEE,
        refundPaidER,
        refundPaidAmount,
        refundPaidDate,
        statePensionAgeDate,
        employeeContributionPercentage,
        employerContributionPercentage,
      } = item;
      const { id, fullName, forename, surname, agencyRef, primaryAgency } =
        subcontractor;
      const employeePercent = isNumber(employeeContributionPercentage)
        ? `${employeeContributionPercentage}%`
        : `${Number(eePercent || 0) * 100}%`;
      const employerPercent = isNumber(employerContributionPercentage)
        ? `${employerContributionPercentage}%`
        : `${Number(erPercent || 0) * 100}%`;
      return {
        employeeId: id,
        name: formatFullName({ fullName, forename, surname }),
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${agencyRef || ''}`,
        statePensionAgeDate: statePensionAgeDate
          ? moment(statePensionAgeDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        companyName: companyPayment?.companyName || '',
        pensionProvider:
          pensionProviderIdOptions.find(
            (provider) => provider?.value === pensionProviderId,
          )?.label || '',
        schemeID: pensionSchemeId || '',
        schemeName: pensionSchemeName || '',
        assessStatus: assessStatus || '',
        assessedAt: assessedAt
          ? moment(assessedAt).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        category: category || '',
        categoryChangedAt: categoryChangedAt
          ? moment(categoryChangedAt).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        postponementEndDate: postponementEndDate
          ? moment(postponementEndDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        ejhDate: ejhDate
          ? moment(ejhDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        employeePercent,
        employerPercent,
        membershipID: memberId || '',
        membershipStarted: membershipStartDate
          ? moment(membershipStartDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        optInDate: otpInDate
          ? moment(otpInDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        optOutDate: otpOutDate
          ? moment(otpOutDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        refundDue: refundDue ? 'Yes' : 'No',
        refundAmount: refundAmount
          ? formatMoneyExportOrNull(
              refundAmount,
              showCurrencyByCountry(payrollCountry),
            )
          : '',
        refundPaidEE:
          isRefundPaid && refundPaidEE
            ? formatMoneyExportOrNull(
                refundPaidEE,
                showCurrencyByCountry(payrollCountry),
              )
            : '',
        refundPaidER:
          isRefundPaid && refundPaidER
            ? formatMoneyExportOrNull(
                refundPaidER,
                showCurrencyByCountry(payrollCountry),
              )
            : '',
        refundPaidAmount:
          isRefundPaid && refundPaidAmount
            ? formatMoneyExportOrNull(
                refundPaidAmount,
                showCurrencyByCountry(payrollCountry),
              )
            : '',
        refundPaidDate:
          isRefundPaid && refundPaidDate
            ? moment(refundPaidDate).tz('Europe/London').format('DD/MM/YYYY')
            : '',
      };
    });
    const headers = [
      'Employee ID',
      'Name',
      'Agency Ref',
      'State Pension Age Date',
      'Payment Company',
      'Pension Provider',
      'Scheme ID',
      'Scheme Name',
      'Status',
      'Last Assessed on',
      'Category',
      'Changed on',
      'Postponement End Date',
      'EJH Date',
      'Employee %',
      'Employer %',
      'Membership ID',
      'Membership Started',
      'Opt-in Date',
      'Opt-out Date',
      'Refund Due?',
      'Refund Amount',
      'Refund Empee Paid',
      'Refund Empr Paid',
      'Total Refund Paid',
      'Refund Paid Date',
    ];

    const csvExporter = new ExportToCsv({
      filename: `Employee-assessed-report-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  useExportAllSocket({
    eventName: `export-all-assessed-process-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        window.open(data.link, '_blank');
        showMessage({
          type: 'success',
          content:
            messages['report.autoEnrollmentStatusReport.exportAllSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: data?.error?.message || 'System Error',
        });
      }
    },
  });
  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    exportPensionAssessed({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['report.autoEnrollmentStatusReport.exportAllPending'],
      });
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds ||
    !!queryParams?.pensionProviderId;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingExport}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 4500, y: 700 }}
            columns={getAutoEnrollmentStatusColumns({
              messages,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <AutoEnrollmentFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default AutoEnrollmentStatusReport;
